import { useEffect, useState } from "react";
import { listLocations, createLocation, updateLocation, deleteLocation, listLocationRequests, reviewLocationRequest } from "@/lib/firestore";
import { LOC } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash, PencilSimple, Plus, X, MapPin, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function Locations() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [binsText, setBinsText] = useState("");

  const load = async () => {
    const [data, reqs] = await Promise.all([listLocations(), listLocationRequests(user)]);
    setItems(data);
    setRequests(reqs.filter((r) => r.status === "pending"));
  };
  useEffect(() => { load(); }, []);

  const review = async (id, action) => {
    try {
      await reviewLocationRequest(id, action, user);
      toast.success(action === "approve" ? "Location created" : "Request rejected");
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const openNew = () => { setEditing(null); setName(""); setDesc(""); setBinsText(""); setShowForm(true); };
  const openEdit = (l) => { setEditing(l); setName(l.name); setDesc(l.description || ""); setBinsText((l.bins || []).join(", ")); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    const bins = binsText.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      if (editing) {
        await updateLocation(editing.id, { name, description: desc, bins });
        toast.success("Location updated");
      } else {
        await createLocation({ name, description: desc, bins }, user);
        toast.success("Location created");
      }
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    try {
      await deleteLocation(id);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-xs text-[#FF4F00] tracking-widest mb-2">// STORAGE MAP</div>
          <h1 className="font-display font-black text-4xl">LOCATIONS & BINS</h1>
          <div className="text-[#A1A1AA] mt-1 text-sm">Aisles, shelves, drawers. Assign bin codes for each location.</div>
        </div>
        <button
          data-testid={LOC.addBtn}
          onClick={openNew}
          className="brutal-btn bg-[#FF4F00] hover:bg-[#FF6B2B] text-black px-4 py-2.5 flex items-center gap-2 transition-colors duration-150"
        >
          <Plus size={16} weight="bold" /> New Location
        </button>
      </div>

      {requests.length > 0 && (
        <div className="brutal-card p-5 border-l-4 border-l-[#FFD700]">
          <div className="flex items-center gap-3 mb-3">
            <h2 className="font-display font-black text-lg">PENDING REQUESTS</h2>
            <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-[#FFD700] text-[#FFD700]">{requests.length}</span>
          </div>
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#1a1a1e] pt-2 first:border-t-0 first:pt-0">
                <div className="text-sm">
                  <span className="font-semibold">{r.name}</span>
                  <span className="text-[#52525B] font-mono text-xs"> · requested by {r.requested_by_name}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => review(r.id, "approve")}
                    className="brutal-btn border border-[#00E676] text-[#00E676] hover:bg-[#00E676] hover:text-black px-3 py-1.5 flex items-center gap-1 text-xs transition-colors duration-150"
                  >
                    <CheckCircle size={12} weight="bold" /> Approve
                  </button>
                  <button
                    onClick={() => review(r.id, "reject")}
                    className="brutal-btn border border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white px-3 py-1.5 flex items-center gap-1 text-xs transition-colors duration-150"
                  >
                    <XCircle size={12} weight="bold" /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="brutal-card p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="font-mono text-xs text-[#FF4F00] tracking-widest">
              // {editing ? "EDIT" : "NEW"} LOCATION
            </div>
            <button onClick={() => setShowForm(false)} className="text-[#A1A1AA] hover:text-white"><X size={18} /></button>
          </div>
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              data-testid={LOC.nameInput}
              placeholder="Name (e.g., Aisle A)"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00] transition-colors duration-150"
            />
            <input
              data-testid={LOC.descInput}
              placeholder="Description (optional)"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00] transition-colors duration-150"
            />
            <input
              data-testid={LOC.binsInput}
              placeholder="Bins (comma-separated e.g. A1, A2, A3)"
              value={binsText}
              onChange={(e) => setBinsText(e.target.value)}
              className="md:col-span-2 bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00] transition-colors duration-150 font-mono"
            />
            <div className="md:col-span-2 flex gap-2">
              <button
                type="submit"
                data-testid={LOC.saveBtn}
                className="brutal-btn bg-[#FF4F00] text-black px-4 py-2.5 hover:bg-[#FF6B2B] transition-colors duration-150"
              >
                {editing ? "Update" : "Create"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="brutal-btn border border-[#27272A] px-4 py-2.5 hover:border-[#FF4F00] transition-colors duration-150">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="col-span-full brutal-card p-8 text-center text-[#52525B] font-mono">
            // no locations yet — add your shop's storage map //
          </div>
        )}
        {items.map((l) => (
          <div key={l.id} className="brutal-card p-5">
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2">
                <MapPin size={14} weight="bold" className="text-[#FF4F00]" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4F00]">LOCATION</div>
              </div>
              <div className="flex gap-1">
                <button
                  data-testid={LOC.editBtn(l.id)}
                  onClick={() => openEdit(l)}
                  className="w-8 h-8 border border-[#27272A] hover:border-[#FF4F00] hover:text-[#FF4F00] flex items-center justify-center transition-colors duration-150"
                >
                  <PencilSimple size={14} />
                </button>
                {user?.role === "admin" && (
                  <button
                    data-testid={LOC.deleteBtn(l.id)}
                    onClick={() => del(l.id)}
                    className="w-8 h-8 border border-[#27272A] hover:border-[#D32F2F] hover:text-[#D32F2F] flex items-center justify-center transition-colors duration-150"
                  >
                    <Trash size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="font-display font-bold text-xl mb-1">{l.name}</div>
            <div className="text-sm text-[#A1A1AA] mb-3">{l.description || "—"}</div>
            <div className="border-t border-[#1a1a1e] pt-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B] mb-2">Bins ({(l.bins || []).length})</div>
              <div className="flex flex-wrap gap-1">
                {(l.bins || []).length === 0 && <span className="text-xs text-[#52525B]">—</span>}
                {(l.bins || []).map((b) => (
                  <span key={b} className="font-mono text-xs border border-[#27272A] px-2 py-0.5 text-[#A1A1AA]">{b}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
